/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Link } from 'react-router-dom';
import { Category } from '../../types';
import { borderRadius, shadows, transitions, typography } from '../../styles/theme';
import {
  TowTruckIcon,
  WrenchIcon,
  MovingTruckIcon,
  CraneIcon,
  ShuttleVanIcon,
  CrownIcon,
  LightbulbIcon,
  NewspaperIcon,
  FileTextIcon,
  ArrowRightIcon,
  IconComponent,
} from '../common';

interface CategoryCardProps {
  category: Category;
  postCount?: number;
}

const cardStyles = css`
  position: relative;
  padding: 1.5rem;
  border-radius: ${borderRadius.xl};
  overflow: hidden;
  transition: all ${transitions.normal};
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 140px;
  color: white;
  box-shadow: ${shadows.sm};

  &:hover {
    color: white;
    transform: translateY(-4px);
    box-shadow: ${shadows.xl};
  }

  /* Beyaz metnin her kategori renginde okunabilmesi için hafif koyulaştırma */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.32) 100%);
    pointer-events: none;
  }
`;

const contentStyles = css`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
`;

const nameStyles = css`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  margin-bottom: 0.25rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
`;

const countStyles = css`
  font-size: ${typography.fontSize.sm};
  opacity: 0.95;
`;

const arrowStyles = css`
  margin-top: auto;
  padding-top: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};

  svg {
    width: 16px;
    height: 16px;
    transition: transform ${transitions.fast};
  }

  a:hover & svg {
    transform: translateX(3px);
  }
`;

const iconStyles = css`
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  width: 56px;
  height: 56px;
  opacity: 0.3;
  z-index: 1;
`;

// Kategori ikonları
const categoryIcons: Record<string, IconComponent> = {
  'cekici-hizmetleri': TowTruckIcon,
  'yol-yardim': WrenchIcon,
  'nakliye': MovingTruckIcon,
  'vinc-hizmetleri': CraneIcon,
  'servis': ShuttleVanIcon,
  'vip-tasima': CrownIcon,
  'ipuclari': LightbulbIcon,
  'haberler': NewspaperIcon,
};

export default function CategoryCard({ category, postCount = 0 }: CategoryCardProps) {
  const Icon = categoryIcons[category.slug] || FileTextIcon;

  return (
    <Link
      to={`/kategori/${category.slug}`}
      css={[cardStyles, css`background: ${category.color};`]}
    >
      <div css={contentStyles}>
        <h3 css={nameStyles}>{category.name}</h3>
        <span css={countStyles}>{postCount > 0 ? `${postCount} yazı` : 'Yakında'}</span>
        <span css={arrowStyles}>
          İncele <ArrowRightIcon />
        </span>
      </div>
      <Icon css={iconStyles} />
    </Link>
  );
}
